import { useApolloClient } from '@apollo/client';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useDependents } from '../../hooks/queries/useDependents';
import { useOnboardingState } from '../../hooks/queries/useOnboardingState';
import {
  getNextScreen,
  getPreviousScreen,
  isAlphaGatedScreen,
  isTerminalScreen,
  OnboardingScreen,
} from '../../lib/onboarding/screens';
import { PATHS } from '../../paths';
import OnboardingLayout from '../OnboardingLayout';
import OnboardingPhaseStepper from '../OnboardingPhaseStepper';

import { seedPendingDependentsFromServer } from './dependents/helpers';
import { OnboardingProvider, type DependentCount } from './OnboardingContext';
import {
  AlmostThereStep,
  DependentCountStep,
  DependentEntryStep,
  DependentResidenceStep,
  DependentReviewStep,
  DependentVehicleStep,
  FinancialAccountConnectStep,
  GreatStartStep,
  PaymentStep,
  PhoneNumberEntryStep,
  PhoneNumberVerifyStep,
  UserResidenceStep,
  WaitlistedStep,
} from './steps';

import type { IOnboardingStepProps } from './types';

/**
 * Typed registry of every onboarding screen to its step component. The
 * `Record<OnboardingScreen, …>` constraint forces every screen to be
 * registered — TypeScript will fail compilation if a new enum value is
 * added without a corresponding entry here. Every screen now has a real
 * component; if a future screen needs a temporary stub, restore the
 * `StubScreenFor` closure factory from the git history.
 */
const SCREEN_COMPONENT_MAP: Record<OnboardingScreen, React.ComponentType<IOnboardingStepProps>> = {
  [OnboardingScreen.PHONE_NUMBER]: PhoneNumberEntryStep,
  [OnboardingScreen.PHONE_NUMBER_VERIFY]: PhoneNumberVerifyStep,
  [OnboardingScreen.USER_RESIDENCE]: UserResidenceStep,
  [OnboardingScreen.GREAT_START]: GreatStartStep,
  [OnboardingScreen.DEPENDENT_COUNT]: DependentCountStep,
  [OnboardingScreen.DEPENDENT_ENTRY]: DependentEntryStep,
  [OnboardingScreen.DEPENDENT_REVIEW]: DependentReviewStep,
  [OnboardingScreen.DEPENDENT_RESIDENCE]: DependentResidenceStep,
  [OnboardingScreen.DEPENDENT_VEHICLE]: DependentVehicleStep,
  [OnboardingScreen.ALMOST_THERE]: AlmostThereStep,
  [OnboardingScreen.PAYMENT]: PaymentStep,
  [OnboardingScreen.FINANCIAL_ACCT_CONNECT]: FinancialAccountConnectStep,
};

interface IBaseOnboardingFlowProps {
  /**
   * Screen the flow mounts on, or `null` to mount straight at the waitlist
   * terminal. The outer `OnboardingFlow` resolves this from
   * `useOnboardingState`.
   */
  initialScreen: OnboardingScreen | null;
  /**
   * `false` for a waitlisted account: the forward walk stops at
   * {@link WaitlistedStep} instead of entering the alpha-gated tail.
   */
  hasAlphaAccess?: boolean;
  /** Called when the user advances past the terminal screen. */
  onComplete: () => void;
}

/**
 * Presentational state-machine container for the new onboarding flow.
 *
 * Drives screen progression with `getNextScreen` / `getPreviousScreen` over
 * `ONBOARDING_SCREEN_ORDER`, looks up the per-screen component in
 * `SCREEN_COMPONENT_MAP`, and renders it inside the shared
 * `OnboardingLayout` with the `OnboardingPhaseStepper` in the header slot.
 *
 * Back navigation is owned by the step component (inline above its title via
 * `OnboardingTitle.onBack`), not the layout — same shape as `LoginFlow`. The
 * orchestrator just threads `onBack` through to the step.
 *
 * Reads from {@link OnboardingProvider} for cross-step state (chosen
 * loved-ones count, edit-mode index, SMS-consent target phone). The
 * provider is mounted one level up in {@link OnboardingFlow} so descendant
 * step components can read and write it.
 *
 * Split from the outer {@link OnboardingFlow} so Storybook can render at any
 * screen without an Apollo backing store.
 */
const BaseOnboardingFlow = ({ initialScreen, hasAlphaAccess = true, onComplete }: IBaseOnboardingFlowProps) => {
  const [currentScreen, setCurrentScreen] = React.useState<OnboardingScreen | null>(initialScreen);

  const handleNext = React.useCallback(() => {
    if (currentScreen === null) {
      return;
    }
    if (isTerminalScreen(currentScreen)) {
      onComplete();
      return;
    }
    const next = getNextScreen(currentScreen);
    if (next === null) {
      return;
    }
    setCurrentScreen(!hasAlphaAccess && isAlphaGatedScreen(next) ? null : next);
  }, [currentScreen, hasAlphaAccess, onComplete]);

  const handleBack = React.useCallback(() => {
    if (currentScreen === null) {
      return;
    }
    const previous = getPreviousScreen(currentScreen);
    if (previous !== null) {
      setCurrentScreen(previous);
    }
  }, [currentScreen]);

  // The waitlist terminal has no phase to point at and nothing after it, so it
  // drops the stepper rather than leaving a progress bar the viewer can't move.
  if (currentScreen === null) {
    return (
      <OnboardingLayout>
        <WaitlistedStep />
      </OnboardingLayout>
    );
  }

  const onBack = getPreviousScreen(currentScreen) !== null ? handleBack : undefined;

  const StepComponent = SCREEN_COMPONENT_MAP[currentScreen];

  return (
    <OnboardingLayout headerSlot={<OnboardingPhaseStepper screen={currentScreen} />}>
      <StepComponent onNext={handleNext} onBack={onBack} />
    </OnboardingLayout>
  );
};

/**
 * The latched `/onboarding` route outcome: redirect a fully-onboarded viewer to
 * the dashboard, or mount the flow at the resumed screen (`null` for a
 * waitlisted viewer with nothing left they're allowed to do).
 */
type OnboardingRouteDecision = { kind: 'redirect' } | { kind: 'flow'; entryScreen: OnboardingScreen | null };

/**
 * Entry component for the new onboarding flow.
 *
 * Resolves the resume screen from `useOnboardingState` and seeds the
 * loved-ones state from the user's persisted dependents (`useDependents`),
 * then hands both to {@link BaseOnboardingFlow} through {@link OnboardingProvider}.
 * Seeding the provider's *initial* state here — rather than from an effect
 * inside the tree — keeps the entry/review forms populated no matter how
 * they're reached (forward, resume, or back-navigation) while the provider
 * stays a plain state container. Both queries are gated below so the seed is
 * synchronous and one-time.
 *
 * Owns the `/onboarding` route decision in a single latch. The first time the
 * onboarding state settles it captures one of three outcomes and holds it in a
 * ref:
 *   - waitlisted (`hasAlphaAccess` false, and either out of gates or resumed
 *     onto a gated screen) → mount the flow at its waitlist terminal. Checked
 *     first: the alpha-gated tail is not theirs to finish, so a waitlisted
 *     viewer never redirects however little is left;
 *   - already fully onboarded (`resumeScreen === null`) → redirect to the
 *     dashboard, so a viewer who hits `/onboarding` directly (bookmark, browser
 *     back, manual URL) isn't shown an empty flow;
 *   - mid-onboarding (`resumeScreen` non-null) → mount the flow at that screen.
 *
 * The decision is latched because `resumeScreen` recomputes live off a
 * `cache-and-network` query and flips to null mid-flow as the user satisfies
 * the gates they were missing — e.g. connecting a financial account derives
 * `null` before they reach the pricing screen that follows it. Reading it live
 * would eject the viewer to the dashboard underneath them. Once latched,
 * progression is owned by {@link BaseOnboardingFlow}'s local screen state and
 * the flow exits only via `onComplete`.
 *
 * Renders nothing until the decision latches (while the onboarding state is
 * still in flight).
 *
 * Mounted at `/onboarding` via `OnboardingScene`.
 */
const OnboardingFlow = () => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const { resumeScreen, hasAlphaAccess, loading } = useOnboardingState();
  const { dependents, loading: dependentsLoading } = useDependents();

  // Latch the route decision the first time the onboarding state settles — see
  // the JSDoc above for why. Reading `resumeScreen` live would eject the viewer
  // to the dashboard the instant they satisfy the final data gate (e.g.
  // connecting an account), before they reach the screens that follow it. A
  // fully-onboarded viewer (resumeScreen null at settle) redirects without
  // waiting on `useDependents` — only the mounted-flow branch needs the seed.
  const decisionRef = React.useRef<OnboardingRouteDecision | null>(null);
  if (decisionRef.current === null && !loading) {
    const waitlisted = !hasAlphaAccess && (resumeScreen === null || isAlphaGatedScreen(resumeScreen));
    if (waitlisted) {
      decisionRef.current = { kind: 'flow', entryScreen: null };
    } else if (resumeScreen === null) {
      decisionRef.current = { kind: 'redirect' };
    } else if (!dependentsLoading) {
      decisionRef.current = { kind: 'flow', entryScreen: resumeScreen };
    }
  }
  const decision = decisionRef.current;

  const handleComplete = React.useCallback(() => {
    void client
      .resetStore()
      .then(() => {
        void navigate(PATHS.DASHBOARD, { replace: true });
      })
      .catch(() => {
        logger.error('Failed to reset Apollo client store after onboarding');
      });
  }, [client, navigate]);

  if (decision === null) {
    return null;
  }
  if (decision.kind === 'redirect') {
    return <Navigate to={PATHS.DASHBOARD} replace />;
  }

  // Seed the loved-ones slots from the user's persisted dependents. The query
  // has resolved by here (gated above), so the provider mounts with the forms
  // already populated — covering forward, resume, and back-nav entry paths.
  // `dependentCount` derives from the seeded array's length inside the provider.
  const seedCount: DependentCount | null = dependents.length === 0 ? null : dependents.length >= 2 ? 2 : 1;
  const initialPendingDependents = seedCount === null ? [] : seedPendingDependentsFromServer(dependents, seedCount);

  return (
    <OnboardingProvider initialPendingDependents={initialPendingDependents}>
      <BaseOnboardingFlow
        initialScreen={decision.entryScreen}
        hasAlphaAccess={hasAlphaAccess}
        onComplete={handleComplete}
      />
    </OnboardingProvider>
  );
};

export default OnboardingFlow;
export { BaseOnboardingFlow };
